import { LinkedinFilled, GithubFilled, UserOutlined } from '@ant-design/icons';

// maintenance mode
export const MAINTENANCE_MODE = false;

// default theme
export const DEFAULT_THEME = 'dark';

// axios request timeout in milliseconds
export const AXIOS_REQUEST_TIMEOUT = 2 * 60 * 1000;

// frontend routes
export const HOME_ROUTE = '/';
export const ABOUT_ROUTE = '/about';
export const PRIVACY_POLICY_ROUTE = '/privacy-policy';
export const COOKIES_POLICY_ROUTE = '/cookies-policy';
export const TERMS_AND_CONDITIONS_ROUTE = '/terms-and-conditions';
export const LOGIN_ROUTE = '/login';
export const SIGN_UP_ROUTE = '/signup';
export const SIGN_UP_REDIRECT_ROUTE = '/signup/redirect';
export const ACTIVATE_USER_ROUTE = '/signup/activate/:token';
export const FORGOT_PASSWORD_ROUTE = '/forgot-password';
export const RESET_PASSWORD_ROUTE = 'reset-password/:token';
export const GITHUB_OAUTH_CALLBACK_ROUTE = '/oauth/github/callback';
export const SETTINGS_ROUTE = '/settings';
export const DASHBOARD_ROUTE = '/dashboard';
export const PROBLEM_SETS_ROUTE = '/problem-sets';
export const PROBLEM_ROUTE = '/problem/:key';
export const NOT_FOUND_ROUTE = '/not-found';

// backend API URLs
export const JWT_CREATE_API_URL = '/accounts/jwt/create/';
export const JWT_REFRESH_API_URL = '/accounts/jwt/refresh/';
export const JWT_VALIDATE_API_URL = '/accounts/jwt/validate/';

export const USER_CREATION_API_URL = '/accounts/users/';
export const USER_ME_INFO_API_URL = '/accounts/users/me/'
export const ACTIVATE_USER_API_URL = '/accounts/users/activate/';
export const RESEND_ACTIVATION_EMAIL_API_URL = '/accounts/users/resend-activation-email/';
export const GITHUB_OAUTH_API_URL = '/accounts/users/github/oauth/';
export const USER_CHANGE_PASSWORD_API_URL = '/accounts/users/change-password/';
export const REQUEST_USER_RESET_PASSWORD_API_URL = '/accounts/users/reset-password/request/';
export const USER_RESET_PASSWORD_API_URL = 'accounts/users/reset-password/';
export const USER_COMMUNICATION_SETTINGS_API_URL = '/accounts/users/settings/communication/';
export const CONTACT_MESSAGE_API_URL = 'accounts/contact/';

export const PROBLEM_SETS_API_URL = '/problems/set/';
export const PROBLEM_API_URL = '/problems/:key/';
export const PROBLEM_SUBMISSION_API_URL = '/problems/:key/submit/';
export const PROBLEM_STATS_API_URL = '/problems/stats/';
export const PROBLEM_LIKE_API_URL = '/problems/:key/like/';
export const PROBLEM_DISLIKE_API_URL = '/problems/:key/dislike/';

// GitHub OAuth
export const GITHUB_OAUTH_AUTHORIZE_URL = 'https://github.com/login/oauth/authorize';
export const GITHUB_OAUTH_SCOPES = [
  'read:user'
];

// navbar donation urls
export const TURKEY_SYRIA_IRC_DONATION_SHOW = false;
export const TURKEY_SYRIA_IRC_DONATION_URL = 'https://www.islamicreliefcanada.org/emergencies/turkey-syria-earthquake-appeal/';
export const PALESTINE_IRC_DONATION_SHOW = false;
export const PALESTINE_IRC_DONATION_URL = 'https://www.islamicreliefcanada.org/emergencies/palestine/';
export const PALESTINE_PCRF_DONATION_SHOW = true;
export const PALESTINE_PCRF_DONATION_URL = 'https://www.pcrf.net/';
export const BANGLADESH_BANNER_SHOW = false;
export const BANGLADESH_ALJAZEERA_LINK = 'https://www.aljazeera.com/program/newsfeed/2024/7/24/bangladeshs-deadly-protests-explained';
export const PALESTINE_BANNER_SHOW = true;
export const PALESTINE_BANNER_LINK = 'https://www.pcrf.net/';
export const PALESTINE_DATASETS_API_BASE_URL = 'https://data.techforpalestine.org';
export const PALESTINE_DATASETS_API_SUMMARY_URL = '/api/v3/summary.min.json';

// local storage
export const ACCESS_TOKEN_LOCALSTORAGE_KEY = 'accessToken';
export const REFRESH_TOKEN_LOCALSTORAGE_KEY = 'refreshToken';
export const GITHUB_OAUTH_STATE_LOCALSTORAGE_KEY = 'githubOAuthState';
export const APPEARANCE_SETTINGS_THEME_LOCALSTORAGE_KEY = 'appearanceSettingsTheme';
export const APPEARANCE_SETTINGS_EDITOR_KEY_BINDINGS_LOCALSTORAGE_KEY = 'editorSettingsKeyBindings';
export const APPEARANCE_SETTINGS_EDITOR_THEME_LOCALSTORAGE_KEY = 'editorSettingsTheme';
export const EDITOR_CACHE_LOCALSTORAGE_KEY = 'editorCodeCache:{userUUID}:{problemID}';

// homepage caption
export const HOMEPAGE_CAPTION = 'Conquer coding puzzles on statistics, machine learning, database systems, array manipulation, and more!';

// interface for a homepage featured problem set
export interface HomepageFeaturedTopic {
  title: string
  caption: string
  iconAlt: string
  iconSrc: string
};

// homepage featured topics
export const HOMEPAGE_FEATURED_TOPICS: HomepageFeaturedTopic[] = [
  {
    title: 'Array Manipulation',
    caption: 'Master proficiency in efficient array operations by solving problems that entail data manipulation and scientific computing.',
    iconAlt: 'Array Manipulation Icon',
    iconSrc: 'https://raw.githubusercontent.com/alvii147/SigmoidMedia/master/homepage/featured_topics/array_manipulation.png'
  },
  {
    title: 'Machine Learning',
    caption: 'Explore the fundamentals of powerful, cutting-edge machine learning methodologies through hands-on learning and practical implementation.',
    iconAlt: 'Machine Learning Icon',
    iconSrc: 'https://raw.githubusercontent.com/alvii147/SigmoidMedia/master/homepage/featured_topics/machine_learning.png'
  },
  {
    title: 'Database Management',
    caption: 'Learn essential skills to effectively query and manipulate databases, utilize SQL for efficient data analysis and decision-making.',
    iconAlt: 'Database Management Icon',
    iconSrc: 'https://raw.githubusercontent.com/alvii147/SigmoidMedia/master/homepage/featured_topics/database_management.png'
  }
];

// about description
export const ABOUT_DESCRIPTION = [
  'Greetings! Zahin here, founder of Sigmoid Academy.',
  'Sigmoid Academy is a data science learning platform that challenges learners to improve their data science knowledge by conquering coding puzzles relating to various concepts, including statistics, machine learning, database systems, array manipulation, and more!',
  'I built Sigmoid Academy in hopes of providing resources for anyone anywhere interested in data science. Education is a wonderful privilege, but should be a human right. For this reason, Sigmoid Academy will always be free for use and will never contain any paid content.'
];

// interface representing tag in about page
export interface AboutTag {
  label: string
  url: string
  Icon: React.FC
  color: string
};

// list of tags in about page
export const ABOUT_TAGS: AboutTag[] = [
  {
    label: 'LinkedIn',
    url: 'https://www.linkedin.com/in/zahin-zaman/',
    Icon: LinkedinFilled,
    color: '#0A66C2'
  },
  {
    label: 'GitHub',
    url: 'https://github.com/alvii147',
    Icon: GithubFilled,
    color: '#181717'
  },
  {
    label: 'Portfolio',
    url: 'https://zahin-zaman.vercel.app',
    Icon: UserOutlined,
    color: '#B4184C'
  }
];

// interface frequently asked question
export interface FrequentlyAskedQuestion {
  question: string,
  answer: string
};

// frequently asked questions array
export const FAQs: FrequentlyAskedQuestion[] = [
  {
    question: 'What is Sigmoid Academy?',
    answer: 'Sigmoid Academy is a data science learning platform that challenges learners to improve their data science knowledge by conquering coding puzzles relating to various concepts, including statistics, machine learning, database systems, array manipulation, and more!'
  },
  {
    question: 'What does a typical coding problem look like?',
    answer: 'A coding problem will usually have a tutorial that explains the concepts required to solve the given problem, a description of the coding task, and a coding editor for submitting your solution. Each of your submissions are tested with multiple test cases to ensure that you\'ve covered basic functionality as well as any corner cases.'
  },
  {
    question: 'Why should I use Sigmoid Academy?',
    answer: 'Sigmoid Academy empowers you to learn data science and machine learning by immersing you in the practical implementation of the core algorithms. This provides you with a hands-on approach that enables you to strengthen your understanding and skills by actively coding and solving data science and machine learning challenges.'
  },
  {
    question: 'Is Sigmoid Academy a paid service?',
    answer: 'No, Sigmoid Academy is completely free for use and always will be. There is no, nor will there ever be, any paid or premium content. Sigmoid Academy was built in hopes of providing resources for anyone anywhere interested in data science. Education is a wonderful privilege, but should be a human right.'
  }
];

// founding year of sigmoid academy
export const FOUNDING_YEAR = 2022;

// interface representing problem difficulty
export interface DifficultyConfig {
  name: string,
  tagClass: string
  tagClassSecondary: string
};

// map of available problem difficulties
export const DIFFICULTY_MAP: Record<string, DifficultyConfig> = {
  'B': {
    name: 'Beginner',
    tagClass: 'difficultyTagSuccess',
    tagClassSecondary: 'difficultyTagSuccessSecondary'
  },
  'I': {
    name: 'Intermediate',
    tagClass: 'difficultyTagWarning',
    tagClassSecondary: 'difficultyTagWarningSecondary'
  },
  'A': {
    name: 'Advanced',
    tagClass: 'difficultyTagDanger',
    tagClassSecondary: 'difficultyTagDangerSecondary'
  }
};

// user avatar URL
export const DICEBEAR_AVATAR_BASE_URL = 'https://api.dicebear.com/5.x/icons/svg';

// number of days after creation for which a problem is considered new
export const NEW_PROBLEM_DAYS = 30;
